import { useState } from 'react';
import { Loader2, Undo2, FileSearch, Sparkles, Pencil } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { effectiveInvoice } from '../../lib/effectiveInvoice';
import type { BvBill, ActualBillLine } from '../../types/billTypes';

type InvoiceCompareDialogProps = {
  open: boolean;
  bill: BvBill | null;
  onClose: () => void;
  /** Drops every correction. Omit to hide the action. */
  onRevert?: () => Promise<void>;
};

const fmt = (n: number | null | undefined) =>
  n == null ? '—' : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

type RowKind = 'same' | 'changed' | 'added' | 'removed';

type Row = {
  kind: RowKind;
  parsed: ActualBillLine | null;
  edited: ActualBillLine | null;
};

/**
 * Builds the paired rows. Positional, because a parsed line and a corrected line
 * have no shared id — the editor works on an array. Inserting or deleting a line
 * therefore shifts everything after it and shows more rows as "changed" than
 * strictly were. That over-reports rather than under-reports, which is the right
 * way round for a screen whose job is to make a difference impossible to miss.
 */
function buildRows(parsed: ActualBillLine[] | undefined, edited: ActualBillLine[] | undefined): Row[] {
  const p = parsed ?? [];
  const e = edited ?? [];
  const rows: Row[] = [];

  for (let i = 0; i < Math.max(p.length, e.length); i++) {
    const pl = p[i] ?? null;
    const el = e[i] ?? null;
    if (pl && !el) { rows.push({ kind: 'removed', parsed: pl, edited: null }); continue; }
    if (!pl && el) { rows.push({ kind: 'added', parsed: null, edited: el }); continue; }
    if (!pl || !el) continue;

    const changed = pl.section !== el.section
      || pl.description !== el.description
      || Math.abs((pl.amount ?? 0) - (el.amount ?? 0)) > 0.005;
    rows.push({ kind: changed ? 'changed' : 'same', parsed: pl, edited: el });
  }
  return rows;
}

/**
 * Parsed against corrected, side by side.
 *
 * Corrections are stored separately from the parse, so both are always available
 * — this is what that buys. A badge saying "corrected by hand" tells someone
 * that a number moved; it does not tell them WHICH number, or from what, and on
 * a document that asserts an overcharge to a paying client that is the part that
 * matters.
 *
 * Unchanged rows are dimmed rather than hidden. A row nobody touched is evidence
 * too — it is how you see that the one changed line is the only changed line.
 */
export const InvoiceCompareDialog = ({ open, bill, onClose, onRevert }: InvoiceCompareDialogProps) => {
  const [reverting, setReverting] = useState(false);
  const [onlyChanged, setOnlyChanged] = useState(false);

  const { result, parsed, source, editedAt, editNote } = effectiveInvoice(bill);
  if (!bill || !parsed) return null;

  const isEdited = source === 'edited';
  const rows = buildRows(parsed.lines, result?.lines);
  const changedCount = rows.filter((r) => r.kind !== 'same').length;
  const shown = onlyChanged ? rows.filter((r) => r.kind !== 'same') : rows;

  const totalChanged = Math.abs((result?.total ?? 0) - (parsed.total ?? 0)) > 0.005;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !reverting) onClose(); }}>
      <DialogContent className="max-w-5xl w-[92vw] max-h-[88vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 py-4 border-b">
          <DialogTitle className="text-base flex items-center gap-2">
            <FileSearch className="h-4 w-4" />
            Parsed vs corrected — {bill.site?.nmi ?? 'invoice'}
          </DialogTitle>
        </DialogHeader>

        {/* Summary strip */}
        <div className="px-5 py-3 border-b bg-gray-50/70 flex items-center gap-4 flex-wrap">
          {isEdited ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 bg-amber-100 border border-amber-200 rounded px-2 py-1">
              <Pencil className="h-3 w-3" />
              {changedCount === 0
                ? 'Corrected by hand — totals only'
                : `${changedCount} of ${rows.length} line${rows.length === 1 ? '' : 's'} changed`}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded px-2 py-1">
              <Sparkles className="h-3 w-3" /> No corrections — these are the parsed values
            </span>
          )}

          {editedAt && (
            <span className="text-xs text-gray-500">
              Corrected {new Date(editedAt).toLocaleString('en-AU', {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </span>
          )}

          {changedCount > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600 ml-auto cursor-pointer">
              <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />
              Show changed only
            </label>
          )}
        </div>

        {/* Why. The whole reason a correction is defensible rather than just
            different — put above the numbers, not tucked beside a timestamp. */}
        {editNote && (
          <div className="px-5 py-3 border-b bg-blue-50/60">
            <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-1">
              Why this was changed
            </p>
            <p className="text-sm text-gray-800 leading-relaxed">{editNote}</p>
          </div>
        )}

        {isEdited && !editNote && (
          <div className="px-5 py-2 border-b bg-gray-50 text-xs text-gray-400">
            No reason was recorded for this correction.
          </div>
        )}

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white shadow-sm">
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pl-5 pr-3 font-medium w-8"></th>
                <th className="py-2 pr-3 font-medium">Description</th>
                <th className="py-2 pr-3 font-medium text-right">
                  <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> Parsed</span>
                </th>
                <th className="py-2 pr-5 font-medium text-right">
                  <span className="inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Corrected</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => {
                const line = r.edited ?? r.parsed;
                const changed = r.kind !== 'same';
                return (
                  <tr
                    key={i}
                    className={cn(
                      'border-b border-gray-100',
                      changed ? 'bg-amber-50/70' : 'text-gray-400',
                    )}
                  >
                    <td className="py-1.5 pl-5 pr-3">
                      {r.kind === 'changed' && <span title="Amount or wording changed" className="text-amber-600">●</span>}
                      {r.kind === 'added' && <span title="Line added by hand" className="text-emerald-600">+</span>}
                      {r.kind === 'removed' && <span title="Line removed by hand" className="text-red-500">−</span>}
                    </td>
                    <td className="py-1.5 pr-3">
                      <span className={cn(changed && 'text-gray-900 font-medium')}>{line?.description}</span>
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-400">{line?.section}</span>
                      {r.kind === 'changed' && r.parsed?.description !== r.edited?.description && (
                        <span className="block text-[10px] text-gray-400 line-through">{r.parsed?.description}</span>
                      )}
                    </td>
                    <td className={cn(
                      'py-1.5 pr-3 text-right tabular-nums',
                      r.kind === 'added' && 'text-gray-300',
                      changed && r.parsed && 'text-gray-500 line-through',
                    )}>
                      {r.parsed ? fmt(r.parsed.amount) : '—'}
                    </td>
                    <td className={cn(
                      'py-1.5 pr-5 text-right tabular-nums',
                      changed ? 'text-amber-900 font-semibold' : '',
                      r.kind === 'removed' && 'text-gray-300',
                    )}>
                      {r.edited ? fmt(r.edited.amount) : '—'}
                    </td>
                  </tr>
                );
              })}

              {shown.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-gray-400">No changed lines.</td></tr>
              )}
            </tbody>

            <tfoot className="sticky bottom-0 bg-white border-t-2 border-gray-300">
              <tr className={cn('font-bold', totalChanged && 'bg-amber-50')}>
                <td></td>
                <td className="py-2.5 pl-0 pr-3">Invoice total</td>
                <td className={cn('py-2.5 pr-3 text-right tabular-nums', totalChanged && 'text-gray-500 line-through')}>
                  {fmt(parsed.total)}
                </td>
                <td className={cn('py-2.5 pr-5 text-right tabular-nums', totalChanged && 'text-amber-900')}>
                  {fmt(result?.total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="px-5 py-3 border-t flex items-center gap-2">
          <p className="text-[11px] text-gray-400 flex-1">
            The parse is never overwritten — corrections are stored beside it, so this comparison is
            always available and can be undone.
          </p>
          {isEdited && onRevert && (
            <Button
              variant="outline"
              size="sm"
              disabled={reverting}
              className="gap-1.5 text-amber-700 hover:bg-amber-50"
              onClick={async () => {
                if (!window.confirm(
                  'Discard every correction and go back to what the parser read?\n\n'
                  + 'The corrected figures will be lost. The parse itself was never changed.',
                )) return;
                setReverting(true);
                try { await onRevert(); onClose(); } finally { setReverting(false); }
              }}
            >
              {reverting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
              Revert to parsed
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
